import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useCopy } from "../lib/copy";

/* ---------------------------------------------------------------- nav */
/* Every page in one place. On a wide screen the links sit in the bar; below
   that they move into a drop-down, because the old narrow bar hid everything
   except the call to action. */
const NAV_LINKS = [
  { to: "/rule", label: "The Rule", testid: "nav-rule-link" },
  { to: "/rule/why", label: "Why Sch\u0113ma?", testid: "nav-essay-link", wide: false },
  { to: "/gatherings", label: "Gatherings", testid: "nav-gatherings-link" },
  { to: "/circles", label: "Circles", testid: "nav-circles-link" },
  { to: "/visit", label: "First visit", testid: "nav-visit-link" },
  { to: "/join", label: "Come to something", testid: "nav-join-link", cta: true },
];

export function Nav({ always = true }) {
  const [show, setShow] = useState(always);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    if (always) {
      setShow(true);
      return undefined;
    }
    const onScroll = () => setShow(window.scrollY > window.innerHeight * 0.6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [always]);

  // A new page means the menu's work is done.
  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <nav
      className={`bar${always ? " always" : ""}${show ? " show" : ""}${
        open ? " menu-open" : ""
      }`}
      data-testid="site-nav"
    >
      <Link to="/" className="mark" data-testid="nav-home-link">
        Sch&#275;ma
      </Link>

      {NAV_LINKS.filter((l) => l.wide !== false).map((l) => (
        <NavLink
          key={l.to}
          to={l.to}
          className={`link${l.cta ? " cta" : ""}`}
          data-testid={l.testid}
        >
          {l.label}
        </NavLink>
      ))}

      <button
        type="button"
        className="bar-toggle"
        aria-expanded={open}
        aria-controls="nav-menu"
        aria-label={open ? "Close the menu" : "Open the menu"}
        onClick={() => setOpen((v) => !v)}
        data-testid="nav-menu-toggle"
      >
        <span className="lab">{open ? "Close" : "Menu"}</span>
        <span className="rules" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
      </button>

      <div
        id="nav-menu"
        className="bar-menu"
        hidden={!open}
        data-testid="nav-menu"
      >
        {NAV_LINKS.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            className={`mlink${l.cta ? " cta" : ""}`}
            data-testid={`nav-menu-${l.testid.replace("nav-", "")}`}
          >
            {l.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

/* ---------------------------------------------------------------- footer */
/*
  The sign-off ornament. The home page keeps the Linotype bar; inner pages get
  one of the small cuts instead, assigned per route so a page always signs off
  with the same mark and the site reads as hand-set rather than templated.
*/
const ROUTE_CUT = {
  "/rule": "chalice",
  "/gatherings": "flame",
  "/circles": "well",
  "/visit": "chalice",
  "/join": "flame",
};
const CUTS = ["well", "flame", "chalice"];

function cutFor(pathname) {
  const p = pathname || "/";
  if (ROUTE_CUT[p]) return ROUTE_CUT[p];
  let h = 0;
  for (let i = 0; i < p.length; i += 1) h = (h * 31 + p.charCodeAt(i)) % 9973;
  return CUTS[h % CUTS.length];
}

export function FooterMark({ cut }) {
  if (cut === "bar") return <Divider />;
  return (
    <div className={`footer-cut cut-${cut}`} aria-hidden="true">
      <i />
    </div>
  );
}

export function Footer({ mark }) {
  const { pathname } = useLocation();
  const c = useCopy();
  const cut = mark || (pathname === "/" ? "bar" : cutFor(pathname));

  return (
    <>
      <FooterMark cut={cut} />
      <footer data-testid="site-footer" data-mark={cut}>
        <div className="wrap">
          <div className="mark-art" aria-hidden="true" />
          <span className="sc">Sch&#275;ma</span>
          <p data-testid="footer-tagline">
            {c(
              "footer.tagline",
              "A community of Christian artists \u00b7 Calgary",
            )}
          </p>
          <p data-testid="footer-note">
            {c(
              "footer.note",
              "A rule, lightly held \u2014 draft, August 2026",
            )}
          </p>
          <p style={{ marginTop: "1.2rem" }}>
            <Link to="/rule">The Rule</Link> &middot;{" "}
            <Link to="/rule/why">Why Sch&#275;ma?</Link> &middot;{" "}
            <Link to="/gatherings">Gatherings</Link> &middot;{" "}
            <Link to="/circles">Circles</Link> &middot;{" "}
            <Link to="/visit">First visit</Link> &middot;{" "}
            <Link to="/join">Come to something</Link>
          </p>
        </div>
      </footer>
    </>
  );
}

/* ---------------------------------------------------------------- ornaments */
export function Divider({ flip = false }) {
  return (
    <div className={`divider${flip ? " flip" : ""}`} aria-hidden="true">
      <div className="art" />
    </div>
  );
}

/* The quiet break. Used for most section changes so the Linotype
   divider stays a rare event (once per page, above the footer). */
export function Rule() {
  return (
    <div className="page-rule" aria-hidden="true">
      <hr className="rule" />
    </div>
  );
}

export function ScatterBand() {
  return (
    <div className="scatter-band" aria-hidden="true">
      <div className="art" />
    </div>
  );
}

export function Eyebrow({ num, children }) {
  return (
    <p className="eyebrow sc">
      <span className="num">{num || "\u00b7"}</span>
      <span>{children}</span>
    </p>
  );
}

export function Verse() {
  return (
    <section>
      <div className="prose">
        <p className="verse">
          &ldquo;The earth is the Lord&rsquo;s and the fullness thereof.&rdquo;
          <cite>Psalm 24:1</cite>
        </p>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- page head */
export function PageHead({ eyebrow, title, standfirst, testid }) {
  return (
    <header className="page-head" data-testid={testid}>
      <div className="prose">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1>{title}</h1>
        {standfirst ? <p className="standfirst">{standfirst}</p> : null}
      </div>
    </header>
  );
}

/* ---------------------------------------------------------------- states */
export function Loading({ label = "Loading", testid = "loading" }) {
  return (
    <div className="loading" data-testid={testid}>
      {label}&hellip;
    </div>
  );
}

export function ErrorNote({ message, onRetry, testid = "error-note" }) {
  return (
    <div className="prose" data-testid={testid}>
      <div className="panel">
        <h3>That didn&rsquo;t load.</h3>
        <p>{message}</p>
        {onRetry ? (
          <button type="button" className="act" onClick={onRetry} data-testid="retry-button">
            Try again
          </button>
        ) : null}
      </div>
    </div>
  );
}

/* Sets <title> and the meta description per route. */
export function useDocumentTitle(title, description) {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = title ? `${title} — Schēma` : "Schēma — a community of Christian artists";
    if (description) {
      let tag = document.querySelector('meta[name="description"]');
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", "description");
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", description);
    }
  }, [title, description, pathname]);
}

/* Adds/removes a class on <body> for the whole life of a page. */
export function useBodyClass(cls) {
  useEffect(() => {
    if (!cls) return undefined;
    const parts = cls.split(" ").filter(Boolean);
    document.body.classList.add(...parts);
    return () => document.body.classList.remove(...parts);
  }, [cls]);
}

/* Every route change should start at the top of the page. */
export function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
